import styled from "@emotion/styled";
import { useRouter } from "next/router";
import * as React from "react";

import {
  SignupForm,
  SignupNavigationConfig,
} from "@brexhq/next/sanity-typegen";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import { parseQueryString } from "@brexhq/next/src/utils/parseQueryString";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { BlockRowItems } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";

import { PartnerRefValues, UserPrefillData } from "../../../routes/signup/Form";
import updateCookie from "../../../utils/updateCookies";

import { FormSubmissionProvider } from "./FormSubmissionProvider";
import { LeadFormModuleProvider } from "./LeadFormModuleProvider";
import { LeadFormStepRenderer } from "./LeadFormStepRenderer";
import {
  ProgressBar,
  ProgressBarBackground,
} from "./Steps/PageTemplates/PageTemplate.elements";
import { useLeadformMachine } from "./hooks/useLeadformMachine";
import { FormMetaProps, FormType } from "./sharedTypes";
import { useBypassSuccess } from "./utils";

/**
 * Used only for /sales and /signup.
 * Here we need a full screen view of the forms
 */
const FullScreenFormContainer = styled.div({
  height: "100vh",
  width: "100%",
});

export type FormSettingsProps = Omit<
  SignupForm,
  "programName" | "submitButtonLabel" | "_type"
> & {
  formExtra?: { textRows: BlockRowItems };
};

export type LeadFormControllerDataProps = {
  leadFormSuccess?: { textRows: BlockRowItems };
  formSettings?: FormSettingsProps;
} & FormMetaProps;
interface LeadFormControllerProps {
  setShowFinalStateView?: React.Dispatch<React.SetStateAction<boolean>>;
  partnerRef?: PartnerRefValues | null;
  userPrefillData?: UserPrefillData;
  data: LeadFormControllerDataProps;
  theme?: ModuleThemeType;
  formTitleOverride?: string;
  navigationConfig?: SignupNavigationConfig;
}

export const LeadFormController: React.FC<LeadFormControllerProps> = ({
  setShowFinalStateView,
  partnerRef,
  userPrefillData,
  data: {
    leadFormSuccess: successContent,
    customProgramName,
    customFormTitle,
    customSubmitButtonLabel,
    bypassSuccessContentUrl,
    isInlineEmail,
    inlineEmailFieldWidth,
    checkWorkEmail,
    hiddenFields,
    formType,
    marginTop,
    formSettings,
  },
  navigationConfig,
  theme = "PrimaryWhite",
}) => {
  const { state, send } = useLeadformMachine(
    customProgramName,
    hiddenFields,
    formType,
    partnerRef,
    isInlineEmail,
  );
  const [progress, setProgress] = React.useState(0);
  const location = useLocation();

  const bypassSuccess = useBypassSuccess();

  const { shouldBypass, link, isInternalLink } = bypassSuccess(
    bypassSuccessContentUrl,
  );
  const router = useRouter();

  React.useEffect(() => {
    const editionValue = parseQueryString("edition", location);
    if (editionValue) {
      updateCookie("edition", editionValue);
    }
  }, [location]);

  React.useEffect(() => {
    if (link && shouldBypass && !isInternalLink) {
      router.prefetch(link);
    }
  }, [isInternalLink, link, router, shouldBypass]);

  /**
   * Two form types exist today.
   * GTM_LEAD_FORM - used via sanity. All Sanity instances of this form will automatically have this type
   * SALES_LEAD_FORM - anywhere else (custom code) will have this.
   * EVENTS_LEAD_FORM - coded, used for offline events, only need basic info
   * Check them out in ./sharedTypes
   */
  const isGTMForm = formType === FormType.GTM_LEAD_FORM;
  const isEventForm = formType === FormType.EVENTS_LEAD_FORM;

  // Determines the validation mode for the form
  const mode = React.useMemo(() => {
    // If the form is an inline email form, we want to submit the form on submit
    if (isInlineEmail) {
      return "onSubmit";
    }
    // If the form has settings, we want to use the configured validation event
    if (formSettings?.validationEvent != null) {
      return formSettings.validationEvent;
    }
    return "onChange";
  }, [isInlineEmail, formSettings]);

  return (
    <FormSubmissionProvider
      send={send}
      programName={customProgramName}
      setProgress={setProgress}
      setShowFinalStateView={
        setShowFinalStateView ? setShowFinalStateView : () => false
      }
      stateMachineCtx={state.context}
      partnerRef={partnerRef}
      userPrefillData={userPrefillData}
      formType={formType as FormType}
      state={state}
      mode={mode}
    >
      <LeadFormModuleProvider
        leadFormSuccess={successContent}
        customFormTitle={customFormTitle}
        customSubmitButtonLabel={customSubmitButtonLabel}
        bypassSuccessContentUrl={bypassSuccessContentUrl}
        customProgramName={customProgramName}
        isInlineEmail={isInlineEmail}
        theme={theme}
        marginTop={marginTop}
        inlineEmailFieldWidth={inlineEmailFieldWidth}
        checkWorkEmail={checkWorkEmail}
        navigationConfig={navigationConfig}
        formSettings={formSettings}
      >
        {!(isGTMForm || isEventForm) && (
          <FullScreenFormContainer>
            {navigationConfig?.showStepProgressBar !== false && (
              <ProgressBarBackground>
                <ProgressBar progress={progress} />
              </ProgressBarBackground>
            )}
            <LeadFormStepRenderer
              formType={formType as FormType}
              state={state}
            />
          </FullScreenFormContainer>
        )}
        {(isGTMForm || isEventForm) && (
          <LeadFormStepRenderer formType={formType as FormType} state={state} />
        )}
      </LeadFormModuleProvider>
    </FormSubmissionProvider>
  );
};
